import { useEffect } from 'react'

import { Stack, Tooltip, Typography } from '@mui/material'
import { Save } from 'lucide-react'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import { Button, Card, Skeleton } from '@repo/ui'

import { InfoTooltip, ScanMediaField, TextField } from 'components'
import { useSnackbar } from 'contexts'
import { useCanAccess } from 'hooks'
import { useCurrentUserCompany } from 'hooks/api'
import { useCompanyMutationsQuery } from 'hooks/api/useCompanyMutationsQuery'

/**
 * VRG-323 — the company-wide fallback for the app's "Where to scan?" panel.
 *
 * A form (rather than apply-on-change) because the instructions are free text and a
 * half-typed sentence is an invalid state that nobody wants persisted — §43's validity
 * question. It takes NO confirmation modal: §43's consequence question asks whether harm
 * accrues while the setting is wrong that you cannot recover, and help copy does not
 * qualify. It corrects forward, unlike a mis-pointed scan destination.
 */
interface ScanDefaultsFormData {
  // Optional, and NOT cosmetically so. `TextField` takes the whole `useForm` return, typed
  // against `FieldValues` (= Record<string, any>); a form type with REQUIRED keys is not
  // assignable to that, so spreading `{...methods}` into it fails to compile. Every field
  // on the Installation page's forms hits exactly that error today. `ProductFormValues` in
  // ProductEditPage is all-optional for the same reason and compiles clean.
  default_scan_instructions?: string
  default_scan_media_url?: string | null
}

/**
 * The DEFAULTS half of Settings → Fields: the values a product inherits when it sets
 * none of its own.
 *
 * It lived on Settings → Installation until this change, which was the wrong home.
 * Installation answers "where does a scan LAND" (subdomain, destination URL, embed
 * snippet); this answers "where on the object do I physically tap". Only the word
 * "scan" connected them. Here it sits beside field mapping, which is the other half of
 * the same subject: what populates a product's fields when the product itself is silent.
 *
 * Self-contained — it owns its company query, its mutation and its form — so the two
 * halves of the Fields page share no state and neither can dirty the other. That also
 * retires the "second instance of useCompanyMutationsQuery" workaround Installation
 * needed: this card is now the only consumer of its own instance, so its success toast
 * cannot fire on a subdomain rename.
 */
export const ScanDefaultsCard = () => {
  const { open: notification } = useSnackbar()
  // `field_default_edit`, not `manage_company`.
  //
  // `manage_company` was carried over verbatim from Installation, where it was correct
  // because everything on that page wrote the company record wholesale. Here it produced
  // a form that nine of the ten roles able to open Fields could read and not save: among
  // company roles only company_admin holds `manage_company`, so moving the card off
  // Installation quietly narrowed who could edit these defaults to one role.
  //
  // These two columns also do not need company-wide write authority. They are help copy
  // shown to a shopper who cannot find a tag — it corrects forward, which is exactly why
  // §43 gives this card a Save and no confirmation. Gating it on the same authority that
  // renames the company and changes its signup domain was over-broad in the other
  // direction.
  const canEditDefaults = useCanAccess({ action: 'field_default_edit' }).isHasAccess

  const { data: companyData, isLoading: isCompanyLoading, refetch: refetchCompany } =
    useCurrentUserCompany()

  const { updateScanDefaults, isLoading: isSaving } = useCompanyMutationsQuery({
    onSuccess: () => {
      notification({
        type: 'success',
        title: 'Default scan instructions saved',
        text: 'Products without their own instructions will use these.',
      })
      refetchCompany()
    },
    onError: (error: any) => {
      notification({
        type: 'error',
        title: 'Save Failed',
        text: error?.message || 'Failed to save the default scan instructions',
      })
    },
  })

  const methods = useForm<ScanDefaultsFormData>({
    defaultValues: { default_scan_instructions: '', default_scan_media_url: null },
  })

  // Seed from the loaded company.
  //
  // `reset`, not `setValue`: reset re-baselines the form so `isDirty` is false right after
  // a load AND right after a save. With setValue the card would come back from a
  // successful save still claiming unsaved changes, and Save would stay enabled forever.
  useEffect(() => {
    if (!companyData) return
    methods.reset({
      default_scan_instructions: companyData.default_scan_instructions ?? '',
      default_scan_media_url: companyData.default_scan_media_url ?? null,
    })
  }, [companyData, methods])

  // Two fields, nothing else.
  //
  // This used to post the whole company record — name, logo, country, provider carried
  // through unchanged — because `PUT /companies/admin/:id` blanks anything omitted. That
  // shape is what forced the `manage_company` gate: the request genuinely WAS a
  // company-wide write, and the endpoint is additionally restricted to
  // platform_admin/platform_manager/company_admin by role, so no amount of permission
  // wiring on this card alone could have let the other roles save.
  //
  // The narrow endpoint below writes only the two columns, so the gate can be the
  // narrow permission, and re-sending four fields this card never edits stops being a
  // thing that can go wrong.
  const handleSave = (data: ScanDefaultsFormData) => {
    if (!companyData?.id) return
    updateScanDefaults({
      companyId: companyData.id,
      // null, not '' — an empty string is a default that says nothing, which the app
      // would still render in place of no default at all.
      default_scan_instructions: data.default_scan_instructions?.trim() || null,
      default_scan_media_url: data.default_scan_media_url || null,
    })
  }

  if (isCompanyLoading) return <ScanDefaultsCardSkeleton />

  return (
    <Card>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSave)}>
          <Stack spacing={5}>
            <Stack spacing={1}>
              <Typography variant="body1" sx={{ fontSize: 20 }}>
                Default scan instructions
              </Typography>
              {/* Two sentences, not one em-dash-joined clause (§15). */}
              <Typography variant="body3" color="text.secondary" sx={{ fontSize: 14, lineHeight: 1.6 }}>
                Used for any product that doesn't set its own. A customer sees this when
                they can't find the tag, so say where it usually sits and show it.
              </Typography>
            </Stack>

            <Stack spacing={5}>
              <Controller
                name="default_scan_instructions"
                control={methods.control}
                // Same single-registration wiring the subdomain field uses: no
                // `{...field}` spread and no `rules` on TextField.
                render={({ field, fieldState }) => (
                  <TextField
                    {...methods}
                    label="Instructions"
                    name="default_scan_instructions"
                    // Multiline for the same reason the per-product field is (ProductEditPage):
                    // this is a sentence or two, and the two fields hold the same kind of copy
                    // against the same contract, so they get the same control.
                    //
                    // `minRows` is inert app-wide (two @mui/material copies in the pnpm store
                    // break TextareaAutosize's height sync — VRG-315), so TextField ALSO states
                    // the height as a floor on the input slot. Keep both: minRows is the right
                    // prop and starts working the day the duplicate is resolved.
                    multiline
                    minRows={3}
                    /*
                      §67 — this field is edited here and felt on a customer's phone, which
                      is the case the rule exists for. The card's description above already
                      says "used for any product that doesn't set its own"; what it cannot
                      say in the space it has is WHERE the customer meets it, or what they
                      get when this is blank. Both belong on the field.

                      `text`, not `term`: there is no glossary entry for "scan
                      instructions" and there should not be one from here — the question
                      this answers is where the setting applies, which is behaviour, not
                      vocabulary (§44). If a term is ever added, it goes in the glossary
                      and this call site gains `term=` alongside the text.
                    */
                    labelAdornment={
                      <InfoTooltip
                        text="Shown in the Vera app when a customer taps “Where to scan?”, on every product that sets no instructions of its own. Left empty, they see Vera's generic line about holding the phone steady against the tag."
                        doc="integrations/settings-field-mapping#a2-the-defaults-tab"
                      />
                    }
                    placeholder="The tag is usually inside the left seam or under the care label."
                    helperText={fieldState.error?.message ?? 'optional'}
                    value={field.value ?? ''}
                    onValueChange={(value) => field.onChange(value)}
                    onFieldBlur={field.onBlur}
                    readOnly={!canEditDefaults}
                  />
                )}
              />

              <Controller
                name="default_scan_media_url"
                control={methods.control}
                render={({ field }) => (
                  <ScanMediaField
                    label="Photo or video"
                    value={field.value}
                    onChange={(url) => field.onChange(url)}
                    disabled={!canEditDefaults}
                    /*
                      §67 again, and it needs its OWN tooltip rather than leaning on the
                      one above: the two tiers resolve INDEPENDENTLY in the app
                      (WhereToScanPanel), so a brand that writes instructions and uploads
                      nothing keeps the plain glyph. That is precisely the "what happens
                      when it is unset" half the rule says people get wrong, and it is not
                      derivable from the instructions field's tooltip.
                    */
                    labelAdornment={
                      <InfoTooltip
                        text="Shown in the same “Where to scan?” panel, on every product that sets no photo or video of its own. Left empty, the customer sees a plain tag icon there instead."
                        doc="integrations/settings-field-mapping#a2-the-defaults-tab"
                      />
                    }
                  />
                )}
              />
            </Stack>

            {/* §27 — visible + disabled with a tooltip naming the missing permission,
                rather than hidden. Same treatment every gated control on Installation gets. */}
            <Stack direction="row" justifyContent="flex-end">
              <Tooltip
                title={canEditDefaults ? '' : "You need the 'Edit defaults' permission. Ask an admin to grant it."}
                arrow
              >
                <span>
                  <Button
                    type="submit"
                    disableElevation
                    startIcon={<Save size={18} strokeWidth={1} />}
                    disabled={!canEditDefaults || !methods.formState.isDirty || isSaving}
                  >
                    {isSaving ? 'Saving…' : 'Save'}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </form>
      </FormProvider>
    </Card>
  )
}

/**
 * §19 footprint parity: the same Card, the same `spacing={5}` rhythm, a title + 2-line
 * description, a 3-row textarea, a 140px media dropzone and a right-aligned Save — so
 * nothing shifts when the company loads.
 */
const ScanDefaultsCardSkeleton = () => (
  <Card>
    <Stack spacing={5}>
      <Stack spacing={1}>
        <Skeleton sx={{ width: 220, height: 24 }} />
        <Skeleton lines={2} sx={{ maxWidth: 520 }} />
      </Stack>
      <Stack spacing={5}>
        <Stack spacing={2}>
          <Skeleton sx={{ width: 88, height: 14 }} />
          {/* 3 rows at the input's 1.4375em line-height ≈ 69px, plus the field's own padding. */}
          <Skeleton sx={{ width: '100%', height: 88, borderRadius: 2 }} />
        </Stack>
        <Stack spacing={2}>
          <Skeleton sx={{ width: 110, height: 14 }} />
          <Skeleton sx={{ width: '100%', height: 140, borderRadius: 2 }} />
        </Stack>
      </Stack>
      <Stack direction="row" justifyContent="flex-end">
        <Skeleton sx={{ width: 96, height: 40, borderRadius: 2 }} />
      </Stack>
    </Stack>
  </Card>
)
